import { Middleware } from 'redux'
import { RootState } from '../modules/rootReducer'
import { TimerActionTypes } from '../modules/timer/types'
import {
  getTimerSyncService,
  TimerSyncMessageType,
  TimerSyncMessage,
} from '../../services/timerSync'
import {
  resetTimer,
  stopTimer,
  pauseTimer,
  resumeTimer,
  startTimer,
  addTime,
  setVisibility,
  restartTimer,
  toggleMode,
} from '../modules/timer/actions'

/**
 * Propriedade especial para marcar ações que vêm de sincronização
 * Isso evita que ações sincronizadas sejam reenviadas, criando loops infinitos
 */
const SYNC_META_KEY = '__fromSync'

/**
 * Middleware que sincroniza ações do timer entre múltiplas abas
 * Quando uma ação do timer é despachada, envia mensagem via BroadcastChannel
 * Quando recebe mensagem de outra aba, despacha a ação correspondente no Redux
 */
export const timerSyncMiddleware: Middleware<any, RootState> = (store) => {
  const syncService = getTimerSyncService()

  // Registra handler para receber mensagens de outras abas
  syncService.onMessage((message: TimerSyncMessage) => {
    try {
      let action: any

      switch (message.type) {
        case TimerSyncMessageType.TIMER_RESET:
          action = resetTimer()
          break

        case TimerSyncMessageType.TIMER_STOP:
          action = stopTimer()
          break

        case TimerSyncMessageType.TIMER_PAUSE:
          action = pauseTimer()
          break

        case TimerSyncMessageType.TIMER_RESUME:
          action = resumeTimer()
          break

        case TimerSyncMessageType.TIMER_START:
          if (message.payload?.sessionId && message.payload?.duration) {
            action = startTimer(message.payload.sessionId, message.payload.duration)
          }
          break

        case TimerSyncMessageType.TIMER_ADD_TIME:
          if (typeof message.payload === 'number') {
            action = addTime(message.payload)
          }
          break

        case TimerSyncMessageType.TIMER_SET_VISIBILITY:
          if (typeof message.payload === 'boolean') {
            action = setVisibility(message.payload)
          }
          break

        case TimerSyncMessageType.TIMER_RESTART:
          action = restartTimer()
          break

        case TimerSyncMessageType.TIMER_TOGGLE_MODE:
          action = toggleMode()
          break

        default:
          if (process.env.NODE_ENV === 'development') {
            console.warn('Tipo de mensagem de sincronização desconhecido:', message.type)
          }
          return
      }

      // Marca a ação como vinda de sincronização para evitar reenvio
      if (action) {
        ;(action as any)[SYNC_META_KEY] = true
        store.dispatch(action)
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao processar mensagem de sincronização:', error)
      }
    }
  })

  return (next) => (action) => {
    // Se a ação veio de sincronização, não reenvia
    // Isso evita loops infinitos
    if ((action as any)[SYNC_META_KEY]) {
      return next(action)
    }

    // Intercepta apenas ações do timer
    if (action.type && action.type.startsWith('@timer/')) {
      // Envia mensagem para outras abas baseado no tipo de ação
      switch (action.type) {
        case TimerActionTypes.RESET_TIMER:
          syncService.sendMessage(TimerSyncMessageType.TIMER_RESET)
          break

        case TimerActionTypes.STOP_TIMER:
          syncService.sendMessage(TimerSyncMessageType.TIMER_STOP)
          break

        case TimerActionTypes.PAUSE_TIMER:
          syncService.sendMessage(TimerSyncMessageType.TIMER_PAUSE)
          break

        case TimerActionTypes.RESUME_TIMER:
          syncService.sendMessage(TimerSyncMessageType.TIMER_RESUME)
          break

        case TimerActionTypes.START_TIMER:
          syncService.sendMessage(TimerSyncMessageType.TIMER_START, {
            sessionId: action.payload?.sessionId,
            duration: action.payload?.duration,
          })
          break

        case TimerActionTypes.ADD_TIME:
          syncService.sendMessage(TimerSyncMessageType.TIMER_ADD_TIME, action.payload)
          break

        case TimerActionTypes.SET_VISIBILITY:
          syncService.sendMessage(TimerSyncMessageType.TIMER_SET_VISIBILITY, action.payload)
          break

        case TimerActionTypes.RESTART_TIMER:
          syncService.sendMessage(TimerSyncMessageType.TIMER_RESTART)
          break

        case TimerActionTypes.TOGGLE_MODE:
          syncService.sendMessage(TimerSyncMessageType.TIMER_TOGGLE_MODE)
          break
      }
    }

    return next(action)
  }
}
